import { useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowUpDown,
  Columns3,
  Info,
  Loader2,
  Pencil,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  disableMfa,
  getLoginHistory,
  getMfaStatus,
  getSessions,
} from "@/lib/auth";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ProfileTable,
  Td,
  Th,
  TablePaginationBar,
} from "./profile-table";
import { TableBody, TableHeader, TableRow } from "@/components/ui/table";
import {
  Field,
  FormSection,
  SectionDivider,
  SettingRow,
  TabHeader,
} from "./profile-form";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { TwoFactorSetupDialog } from "./TwoFactorSetupDialog";
import { SignOutDevicesDialog } from "./SignOutDevicesDialog";
import { sessionIcon, sessionSubtitle } from "./session-display";

const MFA_STATUS_KEY = ["auth", "mfa-status"] as const;

const SSO_LABELS: Record<string, string> = {
  google: "Google",
  facebook: "Facebook",
  apple: "Apple",
  amazon: "Amazon",
};

function formatLoginDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : format(date, "dd MMM yyyy · HH:mm");
}

export function SecurityTab() {
  const { t, i18n } = useLingui();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Federated (SSO) accounts can't use Cognito password/TOTP MFA.
  const ssoProvider =
    user?.authProvider && user.authProvider !== "cognito"
      ? user.authProvider
      : null;
  const ssoLabel = ssoProvider
    ? (SSO_LABELS[ssoProvider] ?? t`your identity provider`)
    : null;
  const [method, setMethod] = useState("authenticator");
  const [isEditing, setIsEditing] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [signOutDevicesOpen, setSignOutDevicesOpen] = useState(false);

  const { data: mfaStatus } = useQuery({
    queryKey: MFA_STATUS_KEY,
    queryFn: getMfaStatus,
    enabled: !ssoProvider,
  });
  const twoFactorEnabled = mfaStatus?.enabled ?? false;

  const { data: sessions = [], isLoading: sessionsLoading } = useQuery({
    queryKey: ["auth", "sessions"],
    queryFn: getSessions,
  });
  const currentSession = sessions.find((s) => s.isCurrent);
  const otherSessions = sessions.filter((s) => !s.isCurrent);

  const { data: loginHistory = [], isLoading: historyLoading } = useQuery({
    queryKey: ["auth", "login-history"],
    queryFn: getLoginHistory,
  });

  const disableMutation = useMutation({
    mutationFn: disableMfa,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MFA_STATUS_KEY });
      toast.success(t`Two-factor authentication disabled`);
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : t`Failed to disable 2FA`,
      );
    },
  });

  const handleToggleTwoFactor = (next: boolean) => {
    if (next) {
      setSetupOpen(true);
    } else {
      disableMutation.mutate();
    }
  };

  return (
    <div className="flex flex-col gap-8">
      <TabHeader
        title={t`Security`}
        description={t`Keep your account protected.`}
        actions={
          isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                <Trans>Cancel</Trans>
              </Button>
              <Button onClick={() => setIsEditing(false)}>
                <Trans>Save</Trans>
              </Button>
            </>
          ) : (
            <Button
              variant="brand-outline"
              onClick={() => setIsEditing(true)}
            >
              <Pencil />
              <Trans>Edit</Trans>
            </Button>
          )
        }
      />

      <FormSection
        title={t`Password`}
        description={t`Use a strong password and update it regularly.`}
      >
        <div className="flex flex-col gap-2">
          <Field label={t`Password`}>
            <div className="flex gap-3">
              <div className="flex h-11 flex-1 items-center rounded-lg border border-(--color-border) bg-(--color-surface-muted) px-3 text-sm text-(--color-text-primary)">
                ••••••••••••••
              </div>
              <Button
                variant="brand-outline"
                onClick={() => setPasswordOpen(true)}
                disabled={!isEditing}
              >
                <RefreshCw />
                <Trans>Reset password</Trans>
              </Button>
            </div>
          </Field>
          <div className="flex items-center gap-2 text-sm text-(--color-text-secondary)">
            <Info className="size-4 text-(--color-text-secondary)" />
            <Trans>Last changed 45 days ago.</Trans>
          </div>
        </div>
      </FormSection>

      <SectionDivider />

      <FormSection
        title={t`Two-factor authentication (2FA)`}
        description={t`Add a second step when signing in.`}
      >
        {ssoLabel ? (
          <div className="flex items-start gap-3 rounded-lg bg-(--color-surface-muted) p-4">
            <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-(--color-surface-accent) text-(--color-text-secondary)">
              <ShieldCheck className="size-6" />
            </div>
            <div>
              <p className="text-sm font-medium text-(--color-text-primary)">
                <Trans>Managed by {ssoLabel}</Trans>
              </p>
              <p className="text-sm text-(--color-text-secondary)">
                <Trans>
                  Two-factor authentication is handled by your {ssoLabel}{" "}
                  account. Manage it in your {ssoLabel} security settings.
                </Trans>
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <div className="flex items-center gap-3">
              <Switch
                checked={twoFactorEnabled}
                onCheckedChange={handleToggleTwoFactor}
                disabled={disableMutation.isPending || !isEditing}
              />
              <span className="text-sm font-medium text-(--color-text-primary)">
                {twoFactorEnabled ? t`Enabled` : t`Disabled`}
              </span>
            </div>
            <Field label={t`Method`}>
              <Select
                value={method}
                onValueChange={setMethod}
                disabled={!isEditing}
              >
                <SelectTrigger className="h-11 rounded-lg border-(--color-border) bg-(--color-surface-content)">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="authenticator">
                    {t`Authenticator app`}
                  </SelectItem>
                  <SelectItem value="sms">{t`SMS`}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        )}
      </FormSection>

      <SectionDivider />

      <FormSection
        title={t`Sessions`}
        description={t`Manage the devices signed in to your account.`}
      >
        <div className="flex flex-col gap-5">
          {sessionsLoading ? (
            <div className="flex items-center gap-2 py-2 text-sm text-(--color-text-secondary)">
              <Loader2 className="size-4 animate-spin" />
              <Trans>Loading sessions…</Trans>
            </div>
          ) : (
            <>
              {currentSession ? (
                <div className="flex flex-col gap-3">
                  <p className="text-sm font-medium text-(--color-text-primary)">
                    <Trans>Current session</Trans>
                  </p>
                  <SettingRow
                    icon={sessionIcon(currentSession.device)}
                    title={currentSession.device}
                    description={
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5">
                          <span className="size-2 rounded-full bg-(--color-success)" />
                          <Trans>Current session</Trans>
                        </span>
                        <span className="text-(--color-text-secondary)">
                          • {sessionSubtitle(currentSession, i18n)}
                        </span>
                      </span>
                    }
                    action={
                      <Button
                        variant="brand-outline"
                        size="sm"
                        onClick={() => setSignOutDevicesOpen(true)}
                        disabled={!isEditing}
                      >
                        <Trans>Sign out other sessions</Trans>
                      </Button>
                    }
                  />
                </div>
              ) : null}

              <div className="flex flex-col gap-3">
                <p className="text-sm font-medium text-(--color-text-primary)">
                  <Trans>Other active sessions</Trans>
                </p>
                {otherSessions.length === 0 ? (
                  <p className="rounded-lg border border-(--color-border) p-4 text-sm text-(--color-text-secondary)">
                    <Trans>No other active sessions.</Trans>
                  </p>
                ) : (
                  otherSessions.map((session) => (
                    <SettingRow
                      key={session.id}
                      icon={sessionIcon(session.device)}
                      title={session.device}
                      description={sessionSubtitle(session, i18n)}
                    />
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </FormSection>

      <SectionDivider />

      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-medium text-(--color-text-primary)">
              <Trans>Login history</Trans>
            </p>
            <p className="text-sm text-(--color-text-secondary)">
              <Trans>Recent sign-ins to your account.</Trans>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-text-secondary)" />
              <input
                className="h-10 w-full rounded-lg border border-(--color-border) bg-(--color-surface-content) pl-9 pr-3 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary) sm:w-64"
                placeholder={t`Search…`}
              />
            </div>
            <Button variant="brand-outline">
              <Columns3 />
              <Trans>Columns</Trans>
            </Button>
            <Button variant="brand-outline">
              <ArrowUpDown />
              <Trans>Sort</Trans>
            </Button>
            <Button variant="brand-outline">
              <SlidersHorizontal />
              <Trans>Filter</Trans>
            </Button>
          </div>
        </div>
        <ProfileTable>
          <TableHeader>
            <TableRow>
              <Th sorted><Trans>Date</Trans></Th>
              <Th><Trans>Device</Trans></Th>
              <Th><Trans>Location</Trans></Th>
              <Th><Trans>IP</Trans></Th>
            </TableRow>
          </TableHeader>
          <TableBody>
            {historyLoading ? (
              <TableRow>
                <Td className="text-(--color-text-secondary)">
                  <Trans>Loading…</Trans>
                </Td>
                <Td>{""}</Td>
                <Td>{""}</Td>
                <Td>{""}</Td>
              </TableRow>
            ) : loginHistory.length === 0 ? (
              <TableRow>
                <Td className="text-(--color-text-secondary)">
                  <Trans>No sign-ins recorded yet.</Trans>
                </Td>
                <Td>{""}</Td>
                <Td>{""}</Td>
                <Td>{""}</Td>
              </TableRow>
            ) : (
              loginHistory.map((row) => (
                <TableRow key={row.id}>
                  <Td>{formatLoginDate(row.createdAt)}</Td>
                  <Td>{row.device}</Td>
                  <Td>{row.location ?? "—"}</Td>
                  <Td>{row.ipAddress ?? "—"}</Td>
                </TableRow>
              ))
            )}
          </TableBody>
        </ProfileTable>
        <TablePaginationBar shown={loginHistory.length} total={loginHistory.length} />
      </div>

      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
      <SignOutDevicesDialog
        open={signOutDevicesOpen}
        onOpenChange={setSignOutDevicesOpen}
      />
      <TwoFactorSetupDialog
        open={setupOpen}
        onOpenChange={setSetupOpen}
        email={user?.email ?? ""}
        onEnabled={() =>
          void queryClient.invalidateQueries({ queryKey: MFA_STATUS_KEY })
        }
      />
    </div>
  );
}
